import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import CompanySelector from './CompanySelector';
import DashboardSelector from './DashboardSelector';
import { useDashboardStale } from '../hooks/useDashboardStale';

const Navbar = () => {
  const navigate = useNavigate();
  const user = useStore((state) => state.user);
  const logout = useStore((state) => state.logout);
  const wsConnected = useStore((state) => state.wsConnected);
  const currentDashboard = useStore((state) => state.currentDashboard);
  const dashboardLastUpdates = useStore((state) => state.dashboardLastUpdates);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isStale = useDashboardStale();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Listen for fullscreen changes to hide navbar
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const getDashboardLastUpdate = () => {
    if (!currentDashboard?.id) return null;
    return dashboardLastUpdates[currentDashboard.id];
  };

  const getConnectionStatus = () => {
    if (!wsConnected) return { label: 'Disconnected', className: 'disconnected' };
    if (isStale) return { label: 'Stale', className: 'stale' };
    return { label: 'Connected', className: 'connected' };
  };

  const formatLastUpdate = () => {
    const lastUpdate = getDashboardLastUpdate();
    if (!lastUpdate) return 'Never';

    try {
      const date = new Date(lastUpdate);
      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'Unknown';
    }
  };

  const status = getConnectionStatus();

  return (
    <nav className={`navbar ${isFullscreen ? 'navbar-hidden' : ''}`}>
      <div className="navbar-left">
        <div className="navbar-brand">
          <h1>Kinspeed Wallboard</h1>
        </div>
      </div>

      <div className="navbar-center">
        <div className="selector-group">
          <div className="selector-wrapper">
            <label className="selector-label">Company</label>
            <CompanySelector />
          </div>
          <div className="selector-wrapper">
            <label className="selector-label">Dashboard</label>
            <DashboardSelector />
          </div>
        </div>
      </div>

      <div className="navbar-right">
        <div className="navbar-status">
          <div className="status-item">
            <span className="status-label">Status:</span>
            <div className="connection-status">
              <div className={`status-indicator ${status.className}`}></div>
              <span>{status.label}</span>
            </div>
          </div>
          <div className="status-item">
            <span className="status-label">Last Update:</span>
            <span className="status-value">{formatLastUpdate()}</span>
          </div>
        </div>

        <div className="desktop-menu">
          <div className="user-info-desktop">
            <span className="user-name">{user?.full_name || user?.username}</span>
            {user?.role === 'super_admin' && <span className="role-badge super-admin">Super Admin</span>}
            {user?.role === 'admin' && <span className="role-badge admin">Admin</span>}
          </div>
          <div className="desktop-menu-actions">
            <button onClick={() => navigate('/')} className="btn-nav">Dashboard</button>
            <button onClick={() => navigate('/settings')} className="btn-nav">Settings</button>
            <button onClick={() => navigate('/profile')} className="btn-nav">Profile</button>
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <>
                <button onClick={() => navigate('/admin/users')} className="btn-nav">Users</button>
                <button onClick={() => navigate('/admin/api-keys')} className="btn-nav">API Keys</button>
              </>
            )}
            {user?.role === 'super_admin' && (
              <button onClick={() => navigate('/admin/companies')} className="btn-nav">Companies</button>
            )}
            <button onClick={handleLogout} className="btn-nav btn-logout-desktop">Logout</button>
          </div>
        </div>

        <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      <div className={`navbar-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="navbar-menu-header">
          <div className="user-info">
            <span className="user-name">{user?.full_name || user?.username}</span>
            {user?.role === 'super_admin' && <span className="role-badge super-admin">Super Admin</span>}
            {user?.role === 'admin' && <span className="role-badge admin">Admin</span>}
          </div>
        </div>
        <div className="navbar-menu-selectors">
          <CompanySelector />
          <DashboardSelector />
        </div>
        <div className="navbar-menu-actions">
          <button onClick={() => navigate('/')}>Dashboard</button>
          <button onClick={() => navigate('/settings')}>Settings</button>
          <button onClick={() => navigate('/profile')}>Profile</button>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <>
              <button onClick={() => navigate('/admin/users')}>Users</button>
              <button onClick={() => navigate('/admin/api-keys')}>API Keys</button>
            </>
          )}
          {user?.role === 'super_admin' && (
            <button onClick={() => navigate('/admin/companies')}>Companies</button>
          )}
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
